import api from './api';

export type PrioridadAnalisis = 'todos' | 'critico' | 'acomprar' | 'sobrestock' | 'sinmovimiento';
export type SnapshotStatus = 'none' | 'processing' | 'ready' | 'error';

export interface ComprasAnalisisCalcularParams {
  nroProveedor?: number;
  codigoRamo?: string;
  codigoLinea?: string;
  codigoClase?: string;
  horizonteDias?: number;
  ajusteDemandaPct?: number;
}

export interface ComprasAnalisisSearchParams {
  nroProveedor?: number;
  codigoRamo?: string;
  codigoLinea?: string;
  codigoClase?: string;
  prioridad?: PrioridadAnalisis;
  searchTerm?: string;
  pageNumber: number;
  pageSize: number;
}

export interface ComprasAnalisisKpis {
  itemsCriticos: number;
  itemsAComprar: number;
  importeEstimado: number;
  coberturaMediana: number;
}

export interface ComprasAnalisisEstado {
  status: SnapshotStatus;
  isRecalculating: boolean;
  generatedAt?: string | null;
  totalItems?: number | null;
  filtrosCalculo?: ComprasAnalisisCalcularParams | null;
  kpis?: ComprasAnalisisKpis | null;
  errorMessage?: string | null;
  durationMs?: number | null;
  calculatedBy?: string | null;
}

export interface ComprasAnalisisItem {
  codigoArticulo: string;
  descripcion: string;
  nroProveedor?: number | null;
  nombreProveedor: string;
  codigoRamo: string;
  codigoLinea: string;
  codigoClase: string;
  stockActual: number;
  stockVenta: number;
  stockPedido: number;
  stockPendienteOc: number;
  stockNeto: number;
  stockMin: number;
  stockMax: number;
  stockObjetivo: number;
  demandaMensual: number;
  demandaDiaria: number;
  coberturaDias: number;
  leadTimeDias: number;
  costoUnitario: number;
  umCompra: string;
  cantidadSugerida: number;
  importeSugerido: number;
  prioridad: string;
  consejo: string;
  ventasMes1: number;
  ventasMes2: number;
  ventasMes3: number;
  ventasMes4: number;
  ventasMes5: number;
  ventasMes6: number;
}

export interface ComprasAnalisisResponse {
  items: ComprasAnalisisItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  kpis: ComprasAnalisisKpis;
  snapshot?: ComprasAnalisisEstado | null;
}

export interface PreviewLineaInput {
  codigoArticulo: string;
  cantidad: number;
  nroProveedor?: number;
  costoUnitario?: number;
}

export interface PreviewGrupoLinea {
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  umCompra: string;
  costoUnitario: number;
  costoBrutoUnitario: number;
  porcDesc1: number;
  porcDesc2: number;
  porcDesc3: number;
  porcDesc4: number;
  porcDesc5: number;
  subtotal: number;
}

export interface PreviewGrupo {
  nroProveedor: number;
  nombreProveedor: string;
  formaDePago: string;
  nroSuc?: number;
  nroUbicacionDestino?: number;
  totalEstimado: number;
  lineas: PreviewGrupoLinea[];
}

export interface PreviewResponse {
  grupos: PreviewGrupo[];
  sinProveedor: PreviewLineaInput[];
}

export interface ComprasAnalisisDemandaMes {
  anio: number;
  mes: number;
  etiqueta: string;
  cantidad: number;
  esMesActual: boolean;
}

export interface ComprasAnalisisDemandaHistorica {
  codigoArticulo: string;
  descripcion: string;
  umCompra: string;
  series: ComprasAnalisisDemandaMes[];
  demandaMensual6m: number;
  demandaPromedio12m: number;
  total12m: number;
  mesMaximoCantidad: number;
  mesMaximoEtiqueta: string;
  mesMinimoCantidad: number;
  mesMinimoEtiqueta: string;
  variacionTendenciaPct: number;
  tendencia: 'Subiendo' | 'Bajando' | 'Estable' | string;
  mesesConDemanda: number;
  coeficienteVariacion: number;
  stockNeto: number;
  coberturaDias: number;
  leadTimeDias: number;
  stockMin: number;
  stockMax: number;
}

export interface ComprasAnalisisStockNetoDeposito {
  nroUbicacion: number;
  nombreUbicacion: string;
  cantidadActual: number;
  stockDisponibleVenta: number;
  stockReservado: number;
  stockProduccion: number;
  stockOrdenCompra: number;
  stockPendienteOcRecibir: number;
  atp: number;
  stockNeto?: number | null;
}

export interface ComprasAnalisisStockNetoComposicion {
  codigoArticulo: string;
  descripcion: string;
  umStock: string;
  stockNeto: number;
  stockPendienteOc: number;
  totales: ComprasAnalisisStockNetoDeposito;
  depositos: ComprasAnalisisStockNetoDeposito[];
}

export interface ComprasAnalisisStockDocumento {
  nroFormulario: number;
  nroItem: number;
  fechaEmision?: string | null;
  comprobante: string;
  entidadNumero?: number | null;
  entidadNombre: string;
  cantidad: number;
}

export interface ComprasAnalisisStockDocumentosResponse {
  items: ComprasAnalisisStockDocumento[];
  total: number;
  /** False si el tipo 12 no descuenta stock: los pedidos se listan pero no comprometen disponible. */
  reservaActiva?: boolean;
}

const comprasAnalisisService = {
  async getEstado(): Promise<ComprasAnalisisEstado> {
    const { data } = await api.get<ComprasAnalisisEstado>('/api/ComprasAnalisis/estado');
    return data;
  },

  async calcular(params: ComprasAnalisisCalcularParams): Promise<ComprasAnalisisEstado> {
    const { data } = await api.post<ComprasAnalisisEstado>('/api/ComprasAnalisis/calcular', {
      nroProveedor: params.nroProveedor,
      codigoRamo: params.codigoRamo || undefined,
      codigoLinea: params.codigoLinea || undefined,
      codigoClase: params.codigoClase || undefined,
      horizonteDias: params.horizonteDias ?? 60,
      ajusteDemandaPct: params.ajusteDemandaPct ?? 0,
    });
    return data;
  },

  async getSugerencias(params: ComprasAnalisisSearchParams): Promise<ComprasAnalisisResponse> {
    const { data } = await api.get<ComprasAnalisisResponse>('/api/ComprasAnalisis/sugerencias', {
      params: {
        nroProveedor: params.nroProveedor || undefined,
        codigoRamo: params.codigoRamo || undefined,
        codigoLinea: params.codigoLinea || undefined,
        codigoClase: params.codigoClase || undefined,
        prioridad: params.prioridad || undefined,
        searchTerm: params.searchTerm || undefined,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      },
    });
    return data;
  },

  async buildPreview(body: {
    lineas: PreviewLineaInput[];
    nroSuc?: number;
    nroUbicacionDestino?: number;
  }): Promise<PreviewResponse> {
    const { data } = await api.post<PreviewResponse>('/api/ComprasAnalisis/preview-ordenes', body);
    return data;
  },

  async getDemandaHistorica(codigoArticulo: string): Promise<ComprasAnalisisDemandaHistorica> {
    const encoded = encodeURIComponent(codigoArticulo.trim());
    const { data } = await api.get<ComprasAnalisisDemandaHistorica>(
      `/api/ComprasAnalisis/demanda-historica/${encoded}`,
    );
    return data;
  },

  async getStockNetoComposicion(codigoArticulo: string): Promise<ComprasAnalisisStockNetoComposicion> {
    const encoded = encodeURIComponent(codigoArticulo.trim());
    const { data } = await api.get<ComprasAnalisisStockNetoComposicion>(
      `/api/ComprasAnalisis/stock-neto/${encoded}`,
    );
    return data;
  },

  async getReservasPedidos(codigoArticulo: string): Promise<ComprasAnalisisStockDocumentosResponse> {
    const encoded = encodeURIComponent(codigoArticulo.trim());
    const { data } = await api.get<ComprasAnalisisStockDocumentosResponse>(
      `/api/ComprasAnalisis/stock-neto/${encoded}/reservas-pedidos`,
    );
    return data;
  },

  async getOcPendientesDetalle(codigoArticulo: string): Promise<ComprasAnalisisStockDocumentosResponse> {
    const encoded = encodeURIComponent(codigoArticulo.trim());
    const { data } = await api.get<ComprasAnalisisStockDocumentosResponse>(
      `/api/ComprasAnalisis/stock-neto/${encoded}/oc-pendientes`,
    );
    return data;
  },
};

export default comprasAnalisisService;
